import React from 'react';
import type { Monitor } from '../../../types/Monitor';
import type { UserMonitorConfig } from '../../../types/UserMonitorConfig';
import {
  formatPrice,
  formatPpi,
  formatResolution,
  formatDiagonal,
  formatRefreshRate,
} from '../../../utils/formatters';
import styles from './MonitorDetailView.module.css';

interface MonitorDetailViewProps {
  monitor: Monitor;
  userConfig: UserMonitorConfig;
  onDismiss: () => void;
}

interface SpecRowProps {
  label: string;
  value: React.ReactNode;
  delta?: string;
  deltaPositive?: boolean;
}

function SpecRow({ label, value, delta, deltaPositive }: SpecRowProps) {
  return (
    <div className={styles.specRow}>
      <span className={styles.specLabel}>{label}</span>
      <span className={styles.specValue}>
        {value}
        {delta && (
          <span
            className={`${styles.delta} ${deltaPositive ? styles.deltaPositive : styles.deltaNeutral}`}
          >
            {delta}
          </span>
        )}
      </span>
    </div>
  );
}

function ppiDelta(target: number, user: number): { text: string; positive: boolean } {
  const diff = target - user;
  const sign = diff >= 0 ? '+' : '\u2212';
  return { text: `${sign}${Math.abs(diff).toFixed(1)} vs yours`, positive: diff >= 0 };
}

function diagonalDelta(target: number, user: number): { text: string; positive: boolean } {
  const diff = target - user;
  const sign = diff >= 0 ? '+' : '\u2212';
  return { text: `${sign}${Math.abs(diff).toFixed(1)}" vs yours`, positive: diff >= 0 };
}

export function MonitorDetailView({ monitor, userConfig, onDismiss }: MonitorDetailViewProps) {
  const userPpi = Math.sqrt(userConfig.nativeResolutionW ** 2 + userConfig.nativeResolutionH ** 2)
    / userConfig.diagonalInches;

  const ppiD  = ppiDelta(monitor.ppi, userPpi);
  const diagD = diagonalDelta(monitor.diagonalInches, userConfig.diagonalInches);

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <span className={styles.brand}>{monitor.brand}</span>
          <h2 className={styles.name}>{monitor.name}</h2>
        </div>
        <button
          className={styles.dismissBtn}
          onClick={onDismiss}
          aria-label="Close detail view"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Spec grid */}
      <div className={styles.specsGrid}>
        <div className={styles.specColumn}>
          <p className={styles.colHeading}>Display</p>
          <SpecRow label="Size" value={formatDiagonal(monitor.diagonalInches)} delta={diagD.text} deltaPositive={diagD.positive} />
          <SpecRow label="Resolution" value={formatResolution(monitor.nativeResolutionW, monitor.nativeResolutionH)} />
          <SpecRow label="Pixel density" value={formatPpi(monitor.ppi)} delta={ppiD.text} deltaPositive={ppiD.positive} />
          <SpecRow label="Panel" value={monitor.panelType} />
          <SpecRow label="Refresh rate" value={formatRefreshRate(monitor.refreshRateHz)} />
        </div>

        <div className={styles.specColumn}>
          <p className={styles.colHeading}>Features &amp; price</p>
          <SpecRow label="HDR" value={monitor.hdr ? 'Yes' : 'No'} />
          <SpecRow label="Brightness" value={monitor.brightnessNits > 0 ? `${monitor.brightnessNits} nits` : '\u2014'} />
          <SpecRow label="Price" value={formatPrice(monitor.priceUSD)} />
          {monitor.ports.length > 0 && (
            <div className={styles.portsRow}>
              <span className={styles.specLabel}>Ports</span>
              <div className={styles.portsList}>
                {monitor.ports.map((p) => (
                  <span key={p} className={styles.portBadge}>{p}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

